import {
    Button,
    Form,
    FormField,
    Input,
    Modal,
    Header,
    Dropdown,
    Checkbox,
} from "grabcad-ui-elements";
import { CustomerInfo, ResellerInfo, Role, OperatingSchedule, CompanyType } from "../../graphql";
import {
    CompanyDataFormField,
    COMPANY_NAME_MAX_LENGTH,
    CustomerDataFormField,
    ResellerDataFormField,
    ICompanyFormProps,
} from "./ConpanyInterface";
import {
    CompanyFormInput,
    CREATE_COMPANY,
    DELETE_COMPANY,
    UPDATE_COMPANY_GLOBAL_ADMIN,
    UPDATE_COMPANY_ANALYZE_DEPLOYMENT_TYPE,
} from "../../graphql/Mutations/Companies";
import { CREATE_EVENT } from "../../graphql/Mutations/Event";
import { parseEmails, emailsInvalidReason } from "../../utils/email";
import {
    EventCategory,
    METRIC_EVENT_CATALOG,
    MetricEventKey,
} from "@grabcad/company-server-shared/dist/types";
import { Notifier } from "../../view/Notifier";
import { alphabetizeByProp } from "../../utils/sort";
import { UserContext } from "../../components/User/UserProvider";
import { config } from "../../config";
import { DeleteEntityConfirmation } from "../../view/Modals/DeleteEntityConfirmation";
import { CompanyLicenseTable } from "../../components/license/LicenseTable";
import { ApplicationContext } from "../../components/ApplicationProvider";
import { TimezoneSelector } from "../../components/TimezoneSelector";
import { OperatingHoursEditor } from "../../components/Company/OperatingHoursEditor";
import { CompanyAccessOptionsTable } from "../../components/CompanyAccessOptionsTable";
import { InfoBox } from "../../components/Styles";
import { NotifyAdminsCheckBox } from "./NotifyAdminsCheckBox";
import { USER_ROLES_QUERY } from "../../graphql/Queries/UserRoles";
import { DropdownProps } from "semantic-ui-react";
import { companyTypeOptions } from "../../utils/constants";
import {
    FunctionComponent,
    FormEvent,
    useContext,
    ReactText,
    useState,
    useCallback,
    useRef,
} from "react";
import { ExecutionResult } from "graphql";
import { cloneDeep } from "lodash";
import { useMutation } from "@apollo/client";
import classnames from "classnames";
import { IScreenProps } from "../Screen";
import {
    CompanyDropdownContainer,
    DropdownLabel,
    DropdownStyle,
    FormContainer,
    FormFieldContainer,
} from "./CompanyStyles";
import Parser from "html-react-parser";

export const CreateCompany: FunctionComponent<IScreenProps> = (props) => {
    return <CompanyForm mutation={CREATE_COMPANY} {...props} />;
};

export const CompanyForm: FunctionComponent<ICompanyFormProps> = ({
    companyInput,
    mutation,
    history,
    location,
    match,
    editing,
    readOnly,
    licenses,
    accessOptions, // <DomainRequestList> also renders <CompanyAccessOptionsTable>, so it's not self contained.
    refetchQueries,
    refetch,
    // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
    const { t } = useContext(ApplicationContext);
    const { role, email, isCompanyAdmin } = useContext(UserContext);
    const [company, setCompany] = useState<CompanyFormInput>(
        companyInput || ({} as CompanyFormInput)
    );

    // companyStateForDirtyTest should always reflect the state of the company in the backend, and is used
    // for the isDirty computation to tell whether the form contents differ from that.
    const [companyStateForDirtyTest, setCompanyStateForDirtyTest] = useState<CompanyFormInput>(
        cloneDeep(company)
    );
    const [emailsString, setEmailsString] = useState<string>(
        companyInput?.admins ? companyInput.admins.join(", ") : ""
    );
    const [submitClicked, setSubmitClicked] = useState<boolean>(false);
    const [operatingHoursUpdated, setOperatingHoursUpdated] = useState<boolean>(false);
    const [confirmRemoveSelfAsAdmin, setConfirmRemoveSelfAsAdmin] = useState<boolean>(false);
    const removingSelfAsAdmin = useRef(false);
    const [companyToDeleteId, setCompanyToDeleteId] = useState<number | undefined>();
    const [notifyAdmins, setNotifyAdmins] = useState<boolean>(true);
    const [analyzeDeploymentType, setAnalyzeDeploymentType] = useState<string | null | undefined>(
        (companyInput as { analyzeDeploymentType?: string | null } | undefined)
            ?.analyzeDeploymentType
    );

    const isCompanyCreation = mutation?.definitions?.some(
        (def) => "name" in def && def.name?.value === "createCompany" //TODO GC-91587 To see if this can be handled in a better way(this used to flag an implicit any error as a result of mutation type being any)
    );
    const isInternalCompany =
        company.internal ||
        (isCompanyCreation &&
            company.admins?.every((adminEmail) =>
                /(@stratasys\.com|@grabcad\.com)$/i.test(adminEmail)
            ));
    const isExternalCompany =
        !isInternalCompany && (company.companyType || "customer") === "customer";

    const [operatingSchedule, setOperatingSchedule] = useState<OperatingSchedule>(
        companyInput?.operatingSchedule || { enabled: false }
    );

    const companyNameField: CompanyDataFormField = {
        labelKey: "company.company_name",
        labelDefault: "Company Name",
        id: "name",
        required: true,
        style: { width: 350 },
        parse: (name: string) => {
            const trimmed = name.trim();

            return trimmed ? name : trimmed;
        },
        invalidator: (name: string) => {
            if (name.length > COMPANY_NAME_MAX_LENGTH) {
                return t("company.invalid_name_too_long", {
                    max_len: COMPANY_NAME_MAX_LENGTH,
                });
            }
        },
    };

    const companyAdminEmailsField: CompanyDataFormField = {
        labelKey: "company.admin_email",
        labelDefault: "Company Admin Emails",
        infoTextKey: "settings.email_disclaimer",
        placeholder: "email@domain.com, email2@domain.com...",
        render: () => emailsString,
        parse: (newEmailsString: string) => {
            setEmailsString(newEmailsString);
            return parseEmails(newEmailsString);
        },
        id: "admins",
        required: true,
        invalidator: (emails: string[]) =>
            !emails.length ? t("forms.field_required") : emailsInvalidReason(emails, t),
        style: {
            width: 530,
        },
    };

    const customerAccountNumberField: CustomerDataFormField = {
        labelDefault: "Ship to Customer Account #",
        id: "accountNumber",
        required: isExternalCompany,
        style: {
            width: 150,
            marginRight: 40,
        },
    };

    const customerNameField: CustomerDataFormField = {
        labelDefault: "Ship to Customer Name",
        id: "name",
        required: false,
        style: {
            width: 150,
            marginRight: 40,
        },
    };

    const customerPhoneField: CustomerDataFormField = {
        labelDefault: "Ship to Customer Phone",
        id: "phoneNumber",
        required: false,
        style: {
            width: 150,
            marginRight: 40,
        },
    };

    const customerEmailField: CustomerDataFormField = {
        labelDefault: "Ship to Customer Email",
        id: "email",
        required: false,
        style: {
            width: 150,
            marginRight: 40,
        },
    };

    const resellerNameField: ResellerDataFormField = {
        labelDefault: "Reseller Name",
        id: "name",
        required: false,
        style: {
            width: 150,
            marginRight: 40,
        },
    };

    const resellerPhoneNumberField: ResellerDataFormField = {
        labelDefault: "Reseller Phone #",
        id: "phoneNumber",
        required: false,
        style: {
            width: 150,
            marginRight: 40,
        },
    };

    const resellerEmailField: ResellerDataFormField = {
        labelDefault: "Reseller Email",
        id: "email",
        required: false,
        style: {
            width: 150,
            marginRight: 40,
        },
    };

    const resellerContactPersonField: ResellerDataFormField = {
        labelDefault: "Reseller Contact Person",
        id: "contactPerson",
        required: false,
        style: {
            width: 150,
            marginRight: 40,
        },
    };

    const resellerAccountNumberField: ResellerDataFormField = {
        labelDefault: "Reseller Account Number",
        id: "accountNumber",
        required: false,
        style: {
            width: 150,
            marginRight: 40,
        },
    };

    const validate = () => {
        const fields = [companyNameField, companyAdminEmailsField] as any;
        return (
            !fields.some(
                (field: CompanyDataFormField) =>
                    (field.required && !company[field.id]) ||
                    (field.invalidator && !!field.invalidator(company[field.id]))
            ) &&
            (!isExternalCompany || !!company.customerInfo?.accountNumber?.trim())
        );
    };

    const renderCustomerInfo = (userRole: string) => {
        if (userRole !== Role.GlobalAdmin) {
            return null;
        }
        const getCustomerFormField = (field: CustomerDataFormField) => {
            let renderedValue: ReactText = "";
            let invalidReason: string | false | undefined = "";
            if (company.customerInfo || isExternalCompany) {
                renderedValue = field.render
                    ? field.render(company.customerInfo?.[field.id])
                    : company.customerInfo?.[field.id] || "";
                invalidReason =
                    submitClicked &&
                    ((field.required &&
                        !company.customerInfo?.[field.id]?.trim() &&
                        t("forms.field_required")) ||
                        field.invalidator?.(company.customerInfo?.[field.id]));
            }
            const updateField = (id: keyof CustomerInfo, event: FormEvent<HTMLInputElement>) => {
                const parsedValue = field.parse
                    ? field.parse(event.currentTarget.value)
                    : event.currentTarget.value;
                let updatedCompany = { ...company };
                updatedCompany.customerInfo = { ...updatedCompany.customerInfo };
                updatedCompany.customerInfo[id] = parsedValue;
                setCompany(updatedCompany);
            };
            return (
                <FormField key={field.id}>
                    <label>
                        {field.labelKey ? t(field.labelKey) : field.labelDefault}
                        {field.required && <span style={{ color: "red" }}>&nbsp;*</span>}
                    </label>
                    <Input
                        id={`qa-company-input-customer_${field.id}`}
                        className={`qa-company-input ${classnames({ error: !!invalidReason })}`}
                        placeholder={field.placeholder}
                        value={renderedValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            updateField(field.id, e)
                        }
                        type={field.type}
                        disabled={field.disabled ? field.disabled() : false}
                        style={field.style}
                    />
                    {invalidReason && (
                        <p
                            id={`qa-company-invalidReason_${field.id}`}
                            className="qa-company-invalidReason ui negative message"
                        >
                            {invalidReason}
                        </p>
                    )}
                </FormField>
            );
        };
        return (
            <div style={{ marginBottom: "10px" }}>
                <div style={{ display: "flex" }}>
                    {getCustomerFormField(customerAccountNumberField)}
                    {getCustomerFormField(customerNameField)}
                    {getCustomerFormField(customerPhoneField)}
                </div>
                <div style={{ display: "flex" }}>{getCustomerFormField(customerEmailField)}</div>
            </div>
        );
    };

    const handleOperatingHoursUpdate = (updatedSchedule: OperatingSchedule) => {
        if (company.operatingSchedule !== updatedSchedule) {
            setOperatingSchedule(updatedSchedule);
            let updatedCompany = { ...company };
            updatedCompany.operatingSchedule = updatedSchedule;
            setCompany(updatedCompany);
            setOperatingHoursUpdated(true);
        }
    };

    const renderOperatingHoursInfo = (userRole: string) => {
        if (userRole !== Role.CompanyAdmin && userRole !== Role.User) {
            return null;
        }

        return (
            <FormField>
                <OperatingHoursEditor
                    schedule={operatingSchedule}
                    onUpdate={handleOperatingHoursUpdate}
                    readOnly={readOnly || userRole === Role.User}
                />
            </FormField>
        );
    };

    const renderTimezoneInfo = () => {
        return (
            <FormField>
                <label>{t("settings.timezone")}</label>
                <TimezoneSelector
                    id="timezone"
                    disabled={!!readOnly}
                    selectedTimezone={company.settings ? company.settings.timezone : ""}
                    onChange={(event: React.SyntheticEvent<HTMLElement>, timezone: string) => {
                        const updatedCompany = cloneDeep(company);
                        updatedCompany.settings = company.settings || {};
                        updatedCompany.settings.timezone = timezone;
                        setCompany(updatedCompany);
                    }}
                />
            </FormField>
        );
    };

    const renderResellerInfo = (userRole: string) => {
        if (userRole !== Role.GlobalAdmin) {
            return null;
        }
        const getResellerDataFormField = (field: ResellerDataFormField) => {
            let renderedValue: ReactText = "";
            let invalidReason: string | boolean | undefined = "";
            if (company.resellerInfo) {
                renderedValue = field.render
                    ? field.render(company.resellerInfo[field.id])
                    : company.resellerInfo[field.id] || "";
                invalidReason =
                    submitClicked && field.invalidator?.(company.resellerInfo[field.id]);
            }
            const updateField = (id: keyof ResellerInfo, event: FormEvent<HTMLInputElement>) => {
                const parsedValue = field.parse
                    ? field.parse(event.currentTarget.value)
                    : event.currentTarget.value;
                let updatedCompany = { ...company };
                updatedCompany.resellerInfo = { ...updatedCompany.resellerInfo };
                updatedCompany.resellerInfo[id] = parsedValue;
                setCompany(updatedCompany);
            };
            return (
                <FormField key={field.id}>
                    <label>{field.labelKey ? t(field.labelKey) : field.labelDefault}</label>
                    <Input
                        id={`qa-company-input-reseller_${field.id}`}
                        className={`qa-company-input ${classnames({ error: !!invalidReason })}`}
                        placeholder={field.placeholder}
                        value={renderedValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            updateField(field.id, e)
                        }
                        type={field.type}
                        disabled={field.disabled ? field.disabled() : false}
                        style={field.style}
                    />
                    {invalidReason && (
                        <p
                            id={`qa-company-invalidReason_${field.id}`}
                            className="qa-company-invalidReason ui negative message"
                        >
                            {invalidReason}
                        </p>
                    )}
                </FormField>
            );
        };
        return (
            <div>
                <div style={{ display: "flex" }}>
                    {getResellerDataFormField(resellerNameField)}
                    {getResellerDataFormField(resellerPhoneNumberField)}
                    {getResellerDataFormField(resellerEmailField)}
                </div>
                <div style={{ display: "flex" }}>
                    {getResellerDataFormField(resellerContactPersonField)}
                    {getResellerDataFormField(resellerAccountNumberField)}
                </div>
            </div>
        );
    };

    const renderCompanyLicenseInfo = (userRole: string) => {
        if (userRole !== Role.GlobalAdmin) {
            // Don't show any license info here for regular users
            return;
        }

        const displayHeader = licenses?.length;
        const header = () => {
            if (displayHeader) {
                return <h3>{t("company.company_licenses")}</h3>;
            }
            return <div />;
        };
        const assignButton = () => {
            return (
                <Button
                    data-testid="qa-company-assignLicense"
                    id="qa-company-assignLicense"
                    type="button"
                    secondary
                    disabled={
                        !companyInput?.id ||
                        (isExternalCompany &&
                            (!company.customerInfo?.accountNumber?.trim() || isDirty))
                    }
                    className="primary right floated"
                    onClick={() => history.push(`/license/assign/${company.id}`)}
                >
                    Assign License
                </Button>
            );
        };

        return (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
                {header()}
                {assignButton()}
            </div>
        );
    };

    const inputComponents: { [id: string]: any } = {
        input: Input,
    };

    const getFormField = (field: CompanyDataFormField) => {
        const InputClass = inputComponents[field.inputComponent || "input"];
        const invalidReason =
            submitClicked &&
            ((field.required && !company[field.id] && t("forms.field_required")) ||
                field.invalidator?.(company[field.id]));

        const renderedValue = field.render
            ? field.render(company[field.id])
            : company[field.id] || "";
        const updateField = (id: keyof CompanyFormInput, event: FormEvent<HTMLInputElement>) => {
            const parsedValue = field.parse
                ? field.parse(event.currentTarget.value)
                : event.currentTarget.value;
            const updatedCompany = { ...company, [id]: parsedValue }; // TODO GC-91587: Remove this hack!!
            setCompany(updatedCompany);
        };
        return (
            <FormField key={field.id}>
                <label>
                    {field.labelKey ? t(field.labelKey) : field.labelDefault}
                    {field.required && <span style={{ color: "red" }}>&nbsp;*</span>}
                </label>
                {field.infoTextKey && <InfoBox>{t(field.infoTextKey)}</InfoBox>}
                <InputClass
                    id={`qa-company-input_${field.id}`}
                    className={`qa-company-input ${classnames({ error: !!invalidReason })}`}
                    placeholder={field.placeholder}
                    value={renderedValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField(field.id, e)}
                    type={field.type}
                    disabled={readOnly || (field.disabled ? field.disabled() : false)}
                    style={field.style}
                />
                {invalidReason && (
                    <p
                        id={`qa-company-invalidReason_${field.id}`}
                        className="qa-company-invalidReason ui negative message"
                    >
                        {invalidReason}
                    </p>
                )}
            </FormField>
        );
    };

    const renderInternalCheckbox = (userRole: string) => {
        if (userRole === Role.GlobalAdmin) {
            return (
                <FormField>
                    <Checkbox
                        id="qa-company-internal"
                        toggle
                        label="Internal Company"
                        checked={company.internal}
                        onChange={() => {
                            let updatedCompany = { ...company };
                            updatedCompany.internal = !updatedCompany.internal;
                            setCompany(updatedCompany);
                        }}
                        style={{ marginTop: 27 }}
                    />
                </FormField>
            );
        }
        return <div />;
    };

    const renderCompanyTypeDropdown = (userRole: string) => {
        const handleCompanyTypeChange = (type: CompanyType) => {
            let updatedCompany = cloneDeep(company);
            updatedCompany.companyType = type;
            setCompany(updatedCompany);
        };
        if (userRole === Role.GlobalAdmin) {
            return (
                <FormField>
                    <DropdownLabel>{t("company.company_type")}</DropdownLabel>
                    <DropdownStyle>
                        <Dropdown
                            id="qa-company-companyType"
                            options={companyTypeOptions}
                            onChange={(
                                event: React.SyntheticEvent<HTMLElement>,
                                data: DropdownProps
                            ) => handleCompanyTypeChange(data?.value as CompanyType)}
                            value={company.companyType || "customer"}
                            selection
                        />
                    </DropdownStyle>
                </FormField>
            );
        }
        return <div />;
    };

    const renderImportFromWb = (userRole: string) => {
        if (userRole !== Role.GlobalAdmin) {
            return <></>;
        }

        return (
            <div style={{ marginTop: "15px" }}>
                <Button
                    id="qa-company-importFromWb"
                    className="ui button primary right floated"
                    type="button"
                    onClick={() => history?.push(`/company/wb/${company.id}`)}
                >
                    Import from WB
                </Button>
            </div>
        );
    };

    const renderDeleteButton = () => {
        if (role === Role.GlobalAdmin && mutation === UPDATE_COMPANY_GLOBAL_ADMIN) {
            return (
                <div style={{ display: "flex" }}>
                    <Button
                        id={`qa-company-delete`}
                        type="submit"
                        negative={true}
                        content={"Delete"}
                        onClick={() => {
                            setCompanyToDeleteId(company.id);
                        }}
                        disabled={isDirty || submitClicked}
                    />
                    <DeleteEntityConfirmation
                        entity={{ id: company.id, name: company.name }}
                        type="Company"
                        mutation={DELETE_COMPANY}
                        update={() => history?.push("/company")}
                        open={!!companyToDeleteId && companyToDeleteId === company.id}
                        onClose={() => setCompanyToDeleteId(undefined)}
                    />
                </div>
            );
        }
        return null;
    };

    const isDirty = JSON.stringify(company) !== JSON.stringify(companyStateForDirtyTest);
    const isValid = validate();
    const closeConfirmRemoveSelfAsAdmin = () => setConfirmRemoveSelfAsAdmin(false);
    const renderCompanyLicenseTable = () => {
        if (role === Role.GlobalAdmin) {
            return (
                <CompanyLicenseTable
                    role={role}
                    licenses={licenses}
                    companyId={company.id}
                    {...{ history, location, match }}
                />
            );
        }
    };

    const renderCompanyAccessOptionsTable = () => {
        if (role === Role.GlobalAdmin && accessOptions?.length) {
            return (
                <>
                    <h3>{t("company.company_access_options")}</h3>
                    <CompanyAccessOptionsTable
                        accessOptions={[...accessOptions].sort(alphabetizeByProp("url"))}
                        role={role}
                        refetch={refetch}
                        companyId={company.id || 0}
                        {...{ history, location, match }}
                    />
                </>
            );
        }
    };

    const [triggerCreateEvent] = useMutation(CREATE_EVENT);

    const [updateDeploymentType, { loading: loadingDeploymentType }] = useMutation(
        UPDATE_COMPANY_ANALYZE_DEPLOYMENT_TYPE,
        {
            onCompleted: (data) => {
                const newType: string | null =
                    data?.updateCompanyAnalyzeDeploymentType?.analyzeDeploymentType ?? null;
                setAnalyzeDeploymentType(newType ?? undefined);
                if (newType) {
                    Notifier.success(t("company.analyze_deployment_type_set_saas"));
                    const { action, description } =
                        METRIC_EVENT_CATALOG[MetricEventKey.ANALYZE_DEPLOYMENT_TYPE_SET_SAAS];
                    void triggerCreateEvent({
                        variables: {
                            events: [{ event: EventCategory.GC_CONTROL, action, description }],
                            companyId: company.id,
                        },
                    }).catch((err) => {
                        console.error("Failed to log analyze deployment type set event:", err);
                    });
                } else {
                    Notifier.success(t("company.analyze_deployment_type_reset"));
                    const { action, description } =
                        METRIC_EVENT_CATALOG[MetricEventKey.ANALYZE_DEPLOYMENT_TYPE_RESET];
                    void triggerCreateEvent({
                        variables: {
                            events: [{ event: EventCategory.GC_CONTROL, action, description }],
                            companyId: company.id,
                        },
                    }).catch((err) => {
                        console.error("Failed to log analyze deployment type reset event:", err);
                    });
                }
            },
            onError: (error) => Notifier.error(error),
        }
    );

    const renderAnalyzeDeploymentTypeSection = (userRole: string) => {
        if (userRole !== Role.GlobalAdmin || !editing) {
            return null;
        }

        const currentTypeLabel =
            analyzeDeploymentType === "saas"
                ? t("company.analyze_deployment_type_saas")
                : analyzeDeploymentType === "onPrem"
                ? t("company.analyze_deployment_type_on_prem")
                : t("company.analyze_deployment_type_none");

        const infoText =
            analyzeDeploymentType === "saas"
                ? t("company.analyze_deployment_type_saas_info")
                : analyzeDeploymentType === "onPrem"
                ? t("company.analyze_deployment_type_reset_info")
                : t("company.analyze_deployment_type_none_info");

        return (
            <div
                style={{
                    border: "1px solid #003393",
                    borderRadius: 4,
                    padding: "16px 20px",
                    margin: "16px 0",
                    background: "#f7f9ff",
                }}
            >
                <label style={{ fontWeight: "bold", display: "block", marginBottom: 6 }}>
                    {t("company.analyze_preference")}
                </label>
                <div style={{ marginBottom: 8 }}>
                    {Parser(t("company.analyze_deployment_type"))}:{" "}
                    <strong>{currentTypeLabel}</strong>
                </div>
                <InfoBox>{Parser(infoText)}</InfoBox>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    {(analyzeDeploymentType === "saas" || analyzeDeploymentType === "onPrem") && (
                        <Button
                            id="qa-company-resetAnalyzeDeploymentType"
                            type="button"
                            secondary
                            disabled={loadingDeploymentType}
                            onClick={() => {
                                void updateDeploymentType({
                                    variables: {
                                        companyId: company.id,
                                        analyzeDeploymentType: null,
                                    },
                                });
                            }}
                        >
                            {t("forms.reset")}
                        </Button>
                    )}
                    {!analyzeDeploymentType && (
                        <Button
                            id="qa-company-setAnalyzeDeploymentTypeSaas"
                            type="button"
                            secondary
                            disabled={loadingDeploymentType}
                            onClick={() => {
                                void updateDeploymentType({
                                    variables: {
                                        companyId: company.id,
                                        analyzeDeploymentType: "saas",
                                    },
                                });
                            }}
                        >
                            {t("company.analyze_deployment_type_set_saas_action")}
                        </Button>
                    )}
                </div>
            </div>
        );
    };

    const sendCompanyChangeEvents = useCallback(
        (updatedCompanyData: CompanyFormInput, previousData: CompanyFormInput) => {
            const eventsToLog: Array<{
                event: EventCategory;
                action: string;
                description: string;
            }> = [];

            // Check if company name changed
            if (updatedCompanyData.name !== previousData.name) {
                const { action, description } =
                    METRIC_EVENT_CATALOG[MetricEventKey.COMPANY_NAME_CHANGED];
                eventsToLog.push({ event: EventCategory.GC_CONTROL, action, description });
            }

            // Check if admin emails changed
            const currentAdmins = JSON.stringify((updatedCompanyData.admins ?? []).slice().sort());
            const previousAdmins = JSON.stringify((previousData.admins ?? []).slice().sort());
            if (currentAdmins !== previousAdmins) {
                const { action, description } =
                    METRIC_EVENT_CATALOG[MetricEventKey.COMPANY_ADMIN_EMAILS_CHANGED];
                eventsToLog.push({ event: EventCategory.GC_CONTROL, action, description });
            }

            // Check if customer account information changed
            const currentCustomer = JSON.stringify(updatedCompanyData.customerInfo);
            const previousCustomer = JSON.stringify(previousData.customerInfo);
            if (currentCustomer !== previousCustomer) {
                const { action, description } =
                    METRIC_EVENT_CATALOG[MetricEventKey.COMPANY_CUSTOMER_ACCOUNT_CHANGED];
                eventsToLog.push({ event: EventCategory.GC_CONTROL, action, description });
            }

            // Trigger all collected events
            if (eventsToLog.length > 0) {
                void triggerCreateEvent({
                    variables: { events: eventsToLog, companyId: updatedCompanyData.id },
                }).catch((err) => {
                    console.error("Failed to log company update events:", err);
                });
            }
        },
        [triggerCreateEvent]
    );

    const [update, { loading: loadingMutation }] = useMutation(mutation, {
        refetchQueries: refetchQueries,
        update: (_cache, { data: { createCompany, updateCompany } }: ExecutionResult<any>) => {
            if (createCompany) {
                history.push(`/company/${createCompany.id}`);
                Notifier.success(t("company.successfully_created"));
            } else if (updateCompany) {
                if (!companyToDeleteId) {
                    let message = operatingHoursUpdated
                        ? t("company.successfully_updated_with_operatinghours")
                        : t("company.successfully_updated");
                    Notifier.success(message);
                    setSubmitClicked(false);
                    setOperatingHoursUpdated(false);
                    if (removingSelfAsAdmin.current) {
                        window.location.assign(`${config.REACT_APP_LOGOUT_URL}`);
                    }
                    // Log events for tracked field changes after successful update
                    // Use setTimeout to ensure this fires outside Apollo's cache update cycle
                    const snapshotCompany = cloneDeep(company);
                    const snapshotPrevious = cloneDeep(companyStateForDirtyTest);
                    setTimeout(() => sendCompanyChangeEvents(snapshotCompany, snapshotPrevious), 0);
                }
                // Make sure isDirty becomes false...
                setCompanyStateForDirtyTest(cloneDeep(company));
            }
        },
        onError: (error) => Notifier.error(error),
    });

    return (
        <Form
            style={{ maxWidth: 550, paddingBottom: 25 }}
            onSubmit={(event) => {
                if (readOnly) {
                    event.preventDefault();
                    return;
                }

                if (!isDirty) {
                    return;
                }

                event.preventDefault();
                setSubmitClicked(true);
                if (isValid && !loadingMutation) {
                    if (
                        role === Role.CompanyAdmin &&
                        company.admins &&
                        !company.admins.includes(email)
                    ) {
                        setConfirmRemoveSelfAsAdmin(true);
                    } else {
                        const vars = isCompanyCreation
                            ? { ...company, notifyAdmins }
                            : { ...company };
                        void update({ variables: vars });
                    }
                }
            }}
        >
            <Modal
                basic
                size="small"
                open={confirmRemoveSelfAsAdmin}
                onClose={closeConfirmRemoveSelfAsAdmin}
            >
                <Header icon="exclamation triangle" content={t("company.remove_yourself.title")} />
                <Modal.Content>
                    <h3>
                        {t("company.remove_yourself.lost_privileges", {
                            email,
                            company_name: company?.name,
                        })}
                    </h3>
                    <p>{t("company.remove_yourself.logged_out")}</p>
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        id="qa-company-removeAdminModal-cancel"
                        secondary
                        onClick={closeConfirmRemoveSelfAsAdmin}
                    >
                        {t("forms.cancel")}
                    </Button>
                    <Button
                        id="qa-company-removeAdminModal-confirm"
                        negative
                        onClick={() => {
                            removingSelfAsAdmin.current = true;
                            // Using an otherwise unnecessary refetchQuery here to brute force logout:
                            void update({
                                variables: company,
                                refetchQueries: [{ query: USER_ROLES_QUERY }],
                            });
                        }}
                    >
                        {t("company.remove_yourself.submit")}
                    </Button>
                </Modal.Actions>
            </Modal>

            <FormContainer>
                <FormFieldContainer>{getFormField(companyNameField)}</FormFieldContainer>
                <CompanyDropdownContainer>
                    {renderCompanyTypeDropdown(role)}
                </CompanyDropdownContainer>
            </FormContainer>
            <FormContainer>
                {renderImportFromWb(role)}
                {renderInternalCheckbox(role)}
            </FormContainer>
            {getFormField(companyAdminEmailsField)}
            {renderCustomerInfo(role)}
            {renderTimezoneInfo()}
            {renderOperatingHoursInfo(role)}
            {renderResellerInfo(role)}
            {renderCompanyLicenseInfo(role)}
            {renderCompanyLicenseTable()}
            {renderCompanyAccessOptionsTable()}
            {renderAnalyzeDeploymentTypeSection(role)}
            <FormField key="buttons">
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginTop: 25,
                    }}
                >
                    {renderDeleteButton()}
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                            id="qa-company-cancel"
                            type="button"
                            secondary
                            disabled={!isValid && submitClicked}
                            onClick={() => history.push("/company")}
                        >
                            {t("forms.cancel")}
                        </Button>
                        <Button
                            id="qa-company-submit"
                            type="submit"
                            className={classnames("primary right floated", {
                                "qa-loading": loadingMutation,
                            })}
                            disabled={
                                readOnly ||
                                !isDirty ||
                                (!isValid && submitClicked) ||
                                (role === Role.CompanyAdmin && !isCompanyAdmin)
                            }
                        >
                            {t("forms.submit")}
                        </Button>
                        {isCompanyCreation && (
                            <NotifyAdminsCheckBox
                                label={t("forms.notify_admins")}
                                checked={notifyAdmins}
                                onClick={() => setNotifyAdmins(!notifyAdmins)}
                            />
                        )}
                    </div>
                </div>
            </FormField>
        </Form>
    );
};
